/* eslint-disable react/jsx-props-no-spreading */
import 'react-day-picker/style.css';
import './calendarCornerRadius.css';

import { DayPicker } from 'react-day-picker';
import type { ChevronProps, DateRange, DayPickerLocale, MonthProps } from 'react-day-picker';

import { useContext } from 'react';
import { isBefore } from 'date-fns';
import { Divider } from '@mui/material';
import { TbIcon } from 'components/Figma/TbIcon';
import { IntlContext } from 'context/LocalisationContext';
import { isValidDate } from '../../utils/dateRange';
import type { CalendarViewProps } from './CalendarView.types';
import { useCalendarStyles } from './calendarStyles';

function MonthWithDivider({ calendarMonth: _calendarMonth, displayIndex, ...divProps }: MonthProps) {
  return (
    <>
      {displayIndex > 0 && <Divider orientation="vertical" flexItem />}
      <div {...divProps} />
    </>
  );
}

function CustomChevron({ orientation }: ChevronProps) {
  if (orientation === 'left') return <TbIcon name="ChevronLeft" size="lg" />;
  if (orientation === 'right') return <TbIcon name="ChevronRight" size="lg" />;
  return <span />;
}

export function CalendarView({
  today,
  onDayClick,
  onDayHover,
  disabled = [],
  numberOfMonths = 1,
  defaultMonth = today,
  disableDateSelection,
  modifiers,
  ...props
}: CalendarViewProps) {
  useCalendarStyles();

  const {
    currentConfig: { dateFnsLocale },
  } = useContext(IntlContext);

  const handleDayClick = (date: Date) => {
    if (disableDateSelection) {
      return;
    }
    onDayClick?.(date);
  };

  const handleDayMouseEnter = (date: Date) => {
    onDayHover?.(date);
  };

  const rawFrom = props.selected?.from;
  const rawTo = props.selected?.to;
  const validFrom = isValidDate(rawFrom) ? rawFrom : undefined;
  const validTo = isValidDate(rawTo) ? rawTo : undefined;
  let normalizedSelected: DateRange | undefined;
  if (validFrom && validTo) {
    normalizedSelected = isBefore(validTo, validFrom)
      ? { from: validTo, to: validFrom }
      : { from: validFrom, to: validTo };
  } else if (validFrom || validTo) {
    normalizedSelected = { from: validFrom ?? validTo, to: undefined };
  } else {
    normalizedSelected = undefined;
  }

  return (
    <DayPicker
      {...props}
      mode="range"
      selected={normalizedSelected}
      onSelect={() => {}}
      today={today}
      defaultMonth={defaultMonth}
      locale={dateFnsLocale as Partial<DayPickerLocale>}
      modifiers={modifiers}
      modifiersClassNames={{
        comparisonStart: 'rdp-comparison_start',
        comparisonEnd: 'rdp-comparison_end',
        comparisonRange: 'rdp-comparison_range',
        comparisonEdge: 'rdp-comparison_edge',
        pendingStart: 'rdp-range_start rdp-range_end',
        weekHoverMiddle: 'rdp-week_hover_middle',
        weekHoverEdge: 'rdp-week_hover_edge',
      }}
      components={{ Month: MonthWithDivider, Chevron: CustomChevron }}
      showWeekNumber
      onDayClick={handleDayClick}
      onDayMouseEnter={handleDayMouseEnter}
      disabled={disabled}
      weekStartsOn={1}
      numberOfMonths={numberOfMonths}
      navLayout="around"
    />
  );
}
